import React, { useState } from 'react';
import { Palette, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const ThemeSelector = () => {
  const { currentTheme, changeTheme, themes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const themeOrder = [
    'signature',
    'ocean',
    'sunset',
    'lavender',
    'forest',
    'midnight',
    'memorial',
    'sunrise',
    'minimal',
    'neon'
  ];

  const handleThemeChange = (themeName) => {
    changeTheme(themeName);
    setIsOpen(false);
  };

  const getThemePreview = (themeName) => {
    const theme = themes[themeName];
    if (!theme) return null;

    return (
      <div className="flex gap-1">
        <div
          className="w-4 h-4 rounded-full border border-gray-300"
          style={{ backgroundColor: theme.cssVars['--sidebar-top'] }}
        />
        <div
          className="w-4 h-4 rounded-full border border-gray-300"
          style={{ backgroundColor: theme.cssVars['--accent-primary'] }}
        />
        <div
          className="w-4 h-4 rounded-full border border-gray-300"
          style={{ backgroundColor: theme.cssVars['--accent-secondary'] }}
        />
      </div>
    );
  };

  return (
    <div className="relative">
      {/* Theme Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors"
        data-testid="theme-selector-button"
      >
        <Palette size={18} className="text-gray-700" />
        <span className="text-sm font-medium text-gray-700">Theme</span>
      </button>

      {/* Theme Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown Panel */}
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-1">Choose Dashboard Theme</h3>
              <p className="text-xs text-gray-600">Select from 10 beautiful themes</p>
            </div>

            <div className="p-2">
              {themeOrder.map((themeName) => {
                const theme = themes[themeName];
                if (!theme) return null;

                return (
                  <button
                    key={themeName}
                    onClick={() => handleThemeChange(themeName)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg mb-1 transition-all ${
                      currentTheme === themeName
                        ? 'bg-blue-50 border-2 border-blue-400'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                    }`}
                    data-testid={`theme-option-${themeName}`}
                  >
                    <div className="flex items-center gap-3">
                      {getThemePreview(themeName)}
                      <span className="text-sm font-medium text-gray-900">{theme.name}</span>
                    </div>
                    {currentTheme === themeName && (
                      <Check size={18} className="text-blue-600" />
                    )}
                  </button>
                );
              })}
            </div>

            <div className="p-3 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                Theme changes apply instantly ⚡
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeSelector;
